import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Grid,
  TextField,
  Button,
  Alert,
  Paper,
  Typography,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Tooltip,
  IconButton
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HistoryIcon from '@mui/icons-material/History';

import PromptHistoryModal from './PromptHistoryModal';
import { authFetch } from '../auth/session';
import AgentFormBasicInfo from './AgentFormBasicInfo';
import AgentFormPromptSection from './AgentFormPromptSection';
import AgentFormModelSettings from './AgentFormModelSettings';
import AgentFormAdvancedSettings from './AgentFormAdvancedSettings';
import AgentFormFallbackSettings from './AgentFormFallbackSettings';
import AgentFormNativeTools from './AgentFormNativeTools';
import AgentFormReadOnlyInfo from './AgentFormReadOnlyInfo';
import {
  AI_PROVIDERS,
  AI_IMAGE_PROVIDERS,
  getModelsByProvider,
  getProviderNames,
  getImageFieldsConfig,
  shouldShowImageDimensions,
  DEFAULT_TEXT_FALLBACK,
  DEFAULT_IMAGE_FALLBACK
} from '../constants/aiModels';

const initialExpandedState = {
  prompt: true,
  modelSettings: true,
  nativeTools: false,
  fallbackSettings: false,
  basicInfo: false,
  advancedSettings: false,
  readOnlyInfo: false,
};

const fetchSpecificAgent = async (agentIdToFetch) => {
  try {
    const res = await authFetch(`${process.env.REACT_APP_API_URL_FLUXI}/api/ms/agent/config`);
    if (!res.ok) {
      let errorMsg = `Error ${res.status} al obtener lista de agentes.`;
      try {
        const errorBody = await res.json();
        errorMsg = errorBody.message || errorMsg;
      } catch(e) {}
      throw new Error(errorMsg);
    }
    const allAgents = await res.json();
    const updatedAgentData = (allAgents || []).find(a => a.agent_id === agentIdToFetch);
    if (!updatedAgentData) {
      throw new Error(`No se encontró el agente actualizado con ID: ${agentIdToFetch}`);
    }
    return updatedAgentData;
  } catch (error) {
    console.error("Error fetching specific agent data:", error);
    throw error;
  }
};

function AgentForm({ agent, fetchAgents, onSaved, isAgentLoading, onFormReady }) {
  const [formData, setFormData] = useState({
    agentId: "",
    description: "",
    prompt: "",
    temperature: 0.7,
    maxTokens: 150,
    topP: 0.9,
    providerAi: "",
    modelAi: "",
    project: "default",
    metadataStr: "{}",
    mcpConfigStr: "null",
    imageDimension: "",
    aspectRatio: "",
    imageSize: "",
    extraParameters: {},
    fallbackConfig: {}
  });

  const [originalData, setOriginalData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [errors, setErrors] = useState({});
  const [metadataError, setMetadataError] = useState(null);
  const [mcpConfigError, setMcpConfigError] = useState(null);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [promptHistory, setPromptHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const [revertSuccessMessage, setRevertSuccessMessage] = useState(null);
  const [expanded, setExpanded] = useState(initialExpandedState);

  const isImageAgent = useMemo(() => {
    return shouldShowImageDimensions(formData.agentId);
  }, [formData.agentId]);

  const aiProviders = useMemo(() => {
    return isImageAgent ? AI_IMAGE_PROVIDERS : AI_PROVIDERS;
  }, [isImageAgent]);

  const availableModels = useMemo(() => {
    if (!formData.providerAi) return [];
    return getModelsByProvider(formData.providerAi, isImageAgent);
  }, [formData.providerAi, isImageAgent]);

  // Dirty state: compare current formData with originalData
  const isDirty = useMemo(() => {
    if (!originalData) return true; // New agent is always "dirty"
    return JSON.stringify(formData) !== JSON.stringify(originalData);
  }, [formData, originalData]);

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded({
      ...expanded,
      [panel]: isExpanded
    });
  };

  const handleFieldChange = (value, fieldName) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
    if (errors[fieldName]) {
      setErrors(prev => ({ ...prev, [fieldName]: null }));
    }
    if (fieldName === 'metadataStr') setMetadataError(null);
    if (fieldName === 'mcpConfigStr') setMcpConfigError(null);
  };

  const handleAutocompleteChange = (value, fieldName) => {
    if (fieldName === 'extra_parameters') {
      const updates = {};

      if (value.resolution !== undefined) {
        updates.imageDimension = value.resolution;
      }
      if (value.aspect_ratio !== undefined) {
        updates.aspectRatio = value.aspect_ratio;
      }
      if (value.image_size !== undefined) {
        updates.imageSize = value.image_size;
      }

      setFormData(prev => ({
        ...prev,
        ...updates,
        extraParameters: { ...prev.extraParameters, ...value }
      }));
      return;
    }

    const newValue = value || "";
    setFormData(prev => ({ ...prev, [fieldName]: newValue }));
    if (errors[fieldName]) {
      setErrors(prev => ({ ...prev, [fieldName]: null }));
    }
    if (fieldName === 'providerAi') {
      setFormData(prev => ({ ...prev, modelAi: "" }));
      if (errors.modelAi) {
        setErrors(prev => ({ ...prev, modelAi: null }));
      }

      const provider = newValue?.toLowerCase();
      if (provider === 'openai') {
        setFormData(prev => {
          const newExtraParams = { ...prev.extraParameters };
          delete newExtraParams.aspect_ratio;
          delete newExtraParams.image_size;
          if (prev.imageDimension) {
            newExtraParams.resolution = prev.imageDimension;
          }
          return {
            ...prev,
            aspectRatio: "",
            imageSize: "",
            extraParameters: newExtraParams
          };
        });
      } else if (provider === 'gemini') {
        setFormData(prev => {
          const newExtraParams = { ...prev.extraParameters };
          delete newExtraParams.resolution;
          if (prev.aspectRatio) {
            newExtraParams.aspect_ratio = prev.aspectRatio;
          }
          if (prev.imageSize) {
            newExtraParams.image_size = prev.imageSize;
          }
          return {
            ...prev,
            imageDimension: "",
            extraParameters: newExtraParams
          };
        });
      } else {
        setFormData(prev => {
          const newExtraParams = { ...prev.extraParameters };
          delete newExtraParams.resolution;
          delete newExtraParams.aspect_ratio;
          delete newExtraParams.image_size;
          return {
            ...prev,
            imageDimension: "",
            aspectRatio: "",
            imageSize: "",
            extraParameters: newExtraParams
          };
        });
      }
    }
  };

  const handleFallbackChange = (newFallbackConfig) => {
    setFormData(prev => ({ ...prev, fallbackConfig: newFallbackConfig }));
  };

  const handleNativeToolChange = (newExtraParams) => {
    setFormData(prev => ({ ...prev, extraParameters: newExtraParams }));
  };

  const buildFormDataFromAgent = (currentAgent) => {
    const extraParams = currentAgent?.preferences?.extra_parameters || {};
    const metadata = currentAgent?.metadata || {};
    const fallbackConfig = metadata.fallback_config || {};

    return {
      agentId: currentAgent?.agent_id || "",
      description: currentAgent?.description || "",
      prompt: currentAgent?.prompt || "",
      temperature: currentAgent?.preferences?.temperature ?? 0.7,
      maxTokens: currentAgent?.preferences?.max_tokens ?? 150,
      topP: currentAgent?.preferences?.top_p ?? 0.9,
      providerAi: currentAgent?.provider_ai || "",
      modelAi: currentAgent?.model_ai || "",
      project: currentAgent?.project || "default",
      metadataStr: currentAgent?.metadata ? JSON.stringify(currentAgent.metadata, null, 2) : "{}",
      mcpConfigStr: currentAgent?.mcp_config ? JSON.stringify(currentAgent.mcp_config, null, 2) : "null",
      imageDimension: extraParams.resolution || "",
      aspectRatio: extraParams.aspect_ratio || "",
      imageSize: extraParams.image_size || "",
      extraParameters: extraParams,
      fallbackConfig: fallbackConfig
    };
  };

  const resetForm = (currentAgent) => {
    const data = buildFormDataFromAgent(currentAgent);
    setFormData(data);
    setOriginalData(currentAgent ? { ...data } : null);

    setMetadataError(null);
    setMcpConfigError(null);
    setResult(null);
    setErrors({});
    setExpanded({ ...initialExpandedState, basicInfo: !currentAgent, advancedSettings: !currentAgent });
    setHistoryModalOpen(false);
    setPromptHistory([]);
    setHistoryError(null);
    setRevertSuccessMessage(null);

    if (onFormReady) {
      setTimeout(() => onFormReady(), 0);
    }
  };

  useEffect(() => {
    resetForm(agent);
  }, [agent]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.agentId.trim()) newErrors.agentId = "El Agent ID es obligatorio.";
    if (!formData.description.trim()) newErrors.description = "La Descripcion es obligatoria.";
    if (!formData.prompt.trim()) newErrors.prompt = "El Prompt es obligatorio.";
    if (!formData.providerAi.trim()) newErrors.providerAi = "El Proveedor AI es obligatorio.";
    if (!formData.modelAi.trim()) newErrors.modelAi = "El Modelo AI es obligatorio.";
    if (!formData.project.trim()) newErrors.project = "El Proyecto es obligatorio.";

    const imageFieldsConfig = getImageFieldsConfig(formData.agentId, formData.providerAi);

    if (imageFieldsConfig.showFields) {
      if (imageFieldsConfig.provider === 'openai') {
        if (!formData.imageDimension || !formData.imageDimension.trim()) {
          newErrors.imageDimension = "La dimension de la imagen es obligatoria.";
        }
      } else if (imageFieldsConfig.provider === 'gemini') {
        if (!formData.aspectRatio || !formData.aspectRatio.trim()) {
          newErrors.aspectRatio = "El Aspect Ratio es obligatorio.";
        }
        if (!formData.imageSize || !formData.imageSize.trim()) {
          newErrors.imageSize = "El Image Size es obligatorio.";
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const safeJsonParse = (jsonString, defaultValue) => {
    if (!jsonString || typeof jsonString !== 'string' || jsonString.trim().toLowerCase() === 'null' || jsonString.trim() === '') {
      return defaultValue;
    }
    try {
      return JSON.parse(jsonString);
    } catch (err) {
      throw new Error(`JSON invalido: ${err.message}`);
    }
  };

  const handleOpenHistory = async () => {
    if (!formData.agentId) return;
    setHistoryModalOpen(true);
    setHistoryLoading(true);
    setHistoryError(null);
    setPromptHistory([]);
    setRevertSuccessMessage(null);

    try {
      const response = await authFetch(`${process.env.REACT_APP_API_URL_FLUXI}/api/ms/agent/config/${formData.agentId}/prompts/history`);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: `Error ${response.status}` }));
        throw new Error(errorData.message || `Error ${response.status}`);
      }
      const data = await response.json();
      setPromptHistory(data || []);
    } catch (error) {
      console.error("Error al cargar historial de prompts:", error);
      setHistoryError(`Error al cargar historial: ${error.message}`);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleRevertPrompt = async (historyId) => {
    const agentIdToRevert = formData.agentId;
    if (!agentIdToRevert || !historyId) return;

    setRevertSuccessMessage(null);

    try {
      const revertResponse = await authFetch(`${process.env.REACT_APP_API_URL_FLUXI}/api/ms/agent/config/${agentIdToRevert}/prompts/revert/${historyId}`, {
        method: 'PUT'
      });

      if (!revertResponse.ok) {
        const errorData = await revertResponse.json().catch(() => ({ message: `Error ${revertResponse.status}` }));
        throw new Error(errorData.message || `Error ${revertResponse.status} al revertir`);
      }

      if (fetchAgents) {
         fetchAgents().catch(err => console.error("Error refrescando lista post-revert (ignorado):", err));
      }

      const updatedAgentData = await fetchSpecificAgent(agentIdToRevert);
      const data = buildFormDataFromAgent(updatedAgentData);
      setFormData(data);
      setOriginalData({ ...data });
      setErrors({});
      setMetadataError(null);
      setMcpConfigError(null);

      setHistoryModalOpen(false);
      setRevertSuccessMessage('Prompt revertido y formulario actualizado!');

      if (onSaved) {
          onSaved(updatedAgentData);
      }

    } catch (error) {
      console.error("Error en el proceso de revertir o refrescar:", error);
      if (!historyModalOpen) {
          setResult({ error: `Error al refrescar datos despues de revertir: ${error.message}` });
      } else {
          throw error;
      }
    }
  };

  useEffect(() => {
    if (revertSuccessMessage) {
      const timer = setTimeout(() => {
        setRevertSuccessMessage(null);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [revertSuccessMessage]);

  const handleSubmitAgent = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMetadataError(null);
    setMcpConfigError(null);
    setErrors({});

    if (!validateForm()) {
      setLoading(false);
      const errorFields = Object.keys(errors);
      if (errorFields.includes('agentId') || errorFields.includes('description') || errorFields.includes('project')) {
        setExpanded(prev => ({ ...prev, basicInfo: true }));
      }
      if (errorFields.includes('prompt')) {
        setExpanded(prev => ({ ...prev, prompt: true }));
      }
      if (errorFields.includes('providerAi') || errorFields.includes('modelAi') ||
          errorFields.includes('imageDimension') || errorFields.includes('aspectRatio') ||
          errorFields.includes('imageSize')) {
        setExpanded(prev => ({ ...prev, modelSettings: true }));
      }
      return;
    }

    let parsedMetadata;
    let parsedMcpConfig;
    try {
      parsedMetadata = safeJsonParse(formData.metadataStr, {});
    } catch (err) {
      setMetadataError(err.message);
      setExpanded(prev => ({ ...prev, advancedSettings: true }));
      setLoading(false);
      return;
    }
    try {
      parsedMcpConfig = safeJsonParse(formData.mcpConfigStr, null);
    } catch (err) {
      setMcpConfigError(err.message);
      setExpanded(prev => ({ ...prev, advancedSettings: true }));
      setLoading(false);
      return;
    }

    // Merge fallback_config into metadata
    const hasFallbackValues = Object.keys(formData.fallbackConfig).length > 0;
    if (hasFallbackValues) {
      parsedMetadata.fallback_config = formData.fallbackConfig;
    }

    const dataToSend = {
      agent_id: formData.agentId,
      description: formData.description,
      prompt: formData.prompt,
      preferences: {
        extra_parameters: formData.extraParameters,
        temperature: formData.temperature,
        max_tokens: formData.maxTokens,
        top_p: formData.topP
      },
      provider_ai: formData.providerAi,
      model_ai: formData.modelAi,
      project: formData.project,
      metadata: parsedMetadata,
      mcp_config: parsedMcpConfig,
    };

    try {
      const response = await authFetch(`${process.env.REACT_APP_API_URL_FLUXI}/api/ms/agent/config`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(dataToSend)
      });
      const resultJson = await response.json();
      setResult(resultJson);

      if (response.ok) {
         // Update originalData to match saved state (clears dirty)
         setOriginalData({ ...formData });
         if (fetchAgents) fetchAgents();
         if (onSaved) onSaved(resultJson);
      } else {
        setResult({ error: `Error ${response.status}: ${resultJson.message || 'Error desconocido'}` });
      }

    } catch (error) {
      setResult({ error: "Error de red o conexion: " + error.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmitAgent} noValidate sx={{ position: 'relative' }}>
      {isAgentLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(230, 230, 230, 0.6)',
            zIndex: 10,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: 1
          }}
        >
          <CircularProgress />
        </Box>
      )}

      <fieldset disabled={isAgentLoading} style={{ border: 'none', padding: 0, margin: 0 }}>
        {/* Sticky save button at top */}
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 5,
            backgroundColor: 'background.paper',
            pb: 1,
            pt: 0.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: isDirty ? '2px solid' : '1px solid',
            borderColor: isDirty ? 'warning.main' : 'divider',
            mb: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={loading || isAgentLoading || !isDirty}
              sx={{ minWidth: 160 }}
            >
              {loading ? (
                <>
                  <CircularProgress size={18} sx={{ mr: 1 }} />
                  Guardando...
                </>
              ) : (
                "Guardar Agente"
              )}
            </Button>
            {isDirty && (
              <Typography variant="caption" color="warning.main" fontWeight="medium">
                Cambios sin guardar
              </Typography>
            )}
          </Box>
          {isImageAgent && (
            <Chip label="Agente de Imagen" size="small" color="secondary" variant="outlined" />
          )}
        </Box>

        {/* 1. Prompt (open) */}
        <Accordion
          expanded={expanded.prompt}
          onChange={handleAccordionChange('prompt')}
          sx={{ mb: 2, ...(errors.prompt ? { border: '1px solid red' } : {}) }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="prompt-content"
            id="prompt-header"
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <Typography variant="h6">Prompt</Typography>
              {agent && (
                <Tooltip title="Ver historial de prompts">
                  <IconButton onClick={handleOpenHistory} size="small" sx={{ ml: 1 }}>
                    <HistoryIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <AgentFormPromptSection
              prompt={formData.prompt}
              errors={errors}
              onFieldChange={handleFieldChange}
              revertSuccessMessage={revertSuccessMessage}
            />
          </AccordionDetails>
        </Accordion>

        {/* 2. Model Settings (open) */}
        <Accordion
          expanded={expanded.modelSettings}
          onChange={handleAccordionChange('modelSettings')}
          sx={{ mb: 2, ...(errors.providerAi || errors.modelAi ? { border: '1px solid red' } : {}) }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="model-settings-content"
            id="model-settings-header"
          >
            <Typography variant="h6">Configuracion del Modelo</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <AgentFormModelSettings
              providerAi={formData.providerAi}
              modelAi={formData.modelAi}
              temperature={formData.temperature}
              maxTokens={formData.maxTokens}
              topP={formData.topP}
              errors={errors}
              availableModels={availableModels}
              aiProviders={aiProviders}
              onFieldChange={handleFieldChange}
              onAutocompleteChange={handleAutocompleteChange}
              imageDimension={formData.imageDimension}
              aspectRatio={formData.aspectRatio}
              imageSize={formData.imageSize}
              agentId={formData.agentId}
              isImageAgent={isImageAgent}
            />
          </AccordionDetails>
        </Accordion>

        {/* 2.5. Native Tools (collapsed, solo para agentes de texto) */}
        {!isImageAgent && (
          <Accordion
            expanded={expanded.nativeTools}
            onChange={handleAccordionChange('nativeTools')}
            sx={{ mb: 2 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Herramientas Nativas del Modelo</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <AgentFormNativeTools
                providerAi={formData.providerAi}
                extraParameters={formData.extraParameters}
                onExtraParametersChange={handleNativeToolChange}
              />
            </AccordionDetails>
          </Accordion>
        )}

        {/* 3. Fallback Settings (collapsed) */}
        <Accordion
          expanded={expanded.fallbackSettings}
          onChange={handleAccordionChange('fallbackSettings')}
          sx={{ mb: 2 }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="fallback-settings-content"
            id="fallback-settings-header"
          >
            <Typography variant="h6">Reintentos y Fallback</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <AgentFormFallbackSettings
              isImageAgent={isImageAgent}
              fallbackConfig={formData.fallbackConfig}
              onFallbackChange={handleFallbackChange}
            />
          </AccordionDetails>
        </Accordion>

        {/* 4. Basic Info (collapsed) */}
        <Accordion
          expanded={expanded.basicInfo}
          onChange={handleAccordionChange('basicInfo')}
          sx={{ mb: 2, ...(errors.agentId || errors.description || errors.project ? { border: '1px solid red' } : {}) }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="basic-info-content"
            id="basic-info-header"
          >
            <Typography variant="h6">Informacion Basica</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <AgentFormBasicInfo
              agentId={formData.agentId}
              project={formData.project}
              description={formData.description}
              errors={errors}
              onFieldChange={handleFieldChange}
            />
          </AccordionDetails>
        </Accordion>

        {/* 5. Advanced Settings (collapsed) */}
        <Accordion
          expanded={expanded.advancedSettings}
          onChange={handleAccordionChange('advancedSettings')}
          sx={{ mb: 2, ...(metadataError || mcpConfigError ? { border: '1px solid red' } : {}) }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="advanced-settings-content"
            id="advanced-settings-header"
          >
            <Typography variant="h6">Configuracion Avanzada</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <AgentFormAdvancedSettings
              metadataStr={formData.metadataStr}
              mcpConfigStr={formData.mcpConfigStr}
              metadataError={metadataError}
              mcpConfigError={mcpConfigError}
              onFieldChange={handleFieldChange}
            />
          </AccordionDetails>
        </Accordion>

        {/* 6. Read-only info (collapsed, only for existing agents) */}
        {agent && (
          <Accordion
            expanded={expanded.readOnlyInfo}
            onChange={handleAccordionChange('readOnlyInfo')}
            sx={{ mb: 2 }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="readonly-info-content"
              id="readonly-info-header"
            >
              <Typography variant="h6">Vista Completa del Agente</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <AgentFormReadOnlyInfo agent={agent} />
            </AccordionDetails>
          </Accordion>
        )}
      </fieldset>

      {result && (
        <Box sx={{ mt: 2 }}>
          <Alert severity={result.error ? "error" : "success"}>
            {result.error ? result.error : "Agente guardado correctamente."}
          </Alert>
          {!result.error && (
            <Paper sx={{ p: 1, mt: 1, backgroundColor: "#f0f0f0", maxHeight: '150px', overflow: 'auto' }}>
              <pre style={{ margin: 0, fontSize: '0.8rem' }}>{JSON.stringify(result, null, 2)}</pre>
            </Paper>
          )}
        </Box>
      )}

      <PromptHistoryModal
        open={historyModalOpen}
        onClose={() => setHistoryModalOpen(false)}
        agentId={formData.agentId}
        historyData={promptHistory}
        isLoading={historyLoading}
        onRevert={handleRevertPrompt}
      />
    </Box>
  );
}

export default AgentForm;
